import { EventEmitter } from 'eventemitter3';
import { isIOS } from 'react-device-detect';
import { Settings } from '../../Settings';
import { isUserFacingCameraTrack } from '../../state/mediaState';
import { AudioEncoderWrapper } from './AudioEncoderWrapper';
import { MediaPacketizer } from './MediaPacketizer';
import { VideoEncoderWrapper } from './VideoEncoderWrapper';
export class MediaProducer extends EventEmitter {
    videoEncoderWrapper;
    audioEncoderWrapper;
    mediaPacketizer;
    videoDataCounter = 0;
    audioDataCounter = 0;
    firstFrameTimestamp = -1;
    currentMediaStreamId;
    screenSharing = false;
    lastAudioDataTimestamp = -1;
    lastVideoDataTimestamp = -1;
    videoEncodingQuality = 'sd';
    constructor() {
        super();
        this.videoEncoderWrapper = new VideoEncoderWrapper();
        this.videoEncoderWrapper.on('videoData', (videoData) => {
            this.onEncodedVideoData(videoData);
        });
        this.audioEncoderWrapper = new AudioEncoderWrapper();
        this.audioEncoderWrapper.on('audioData', async (audioData) => {
            await this.onEncodedAudioData(audioData);
        });
        this.mediaPacketizer = new MediaPacketizer();
        this.mediaPacketizer.on('mediaPacketFragment', (fragment) => {
            this.emit('mediaPacketFragment', fragment);
        });
    }
    // May be called multiple times
    async start(stream) {
        console.log('MediaProducer::start(), stream', stream);
        this.firstFrameTimestamp = -1;
        if (this.currentMediaStreamId !== undefined &&
            this.currentMediaStreamId !== stream.id) {
            console.log('mediaStreamId changed', this.currentMediaStreamId);
        }
        this.currentMediaStreamId = stream.id;
        console.log('tracks2', stream.getTracks());
        try {
            const videoTrack = stream.getVideoTracks()[0];
            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) {
                const settings = audioTrack.getSettings();
                console.log('Audio track sample rate:', settings.sampleRate);
                await this.audioEncoderWrapper.start(audioTrack);
            }
            if (videoTrack) {
                this.mediaPacketizer.setIsVideoActive(true);
                this.screenSharing =
                    videoTrack.getSettings().displaySurface != null;
                await this.videoEncoderWrapper.start(videoTrack);
            }
            else {
                this.mediaPacketizer.setIsVideoActive(false);
            }
        }
        catch (error) {
            console.error('Failed to start MediaProducer', error);
        }
    }
    onEncodedVideoData(videoData) {
        if (this.firstFrameTimestamp === -1) {
            this.firstFrameTimestamp = Number(videoData.timestamp);
        }
        const adjustedTimestamp = Number(videoData.timestamp) - this.firstFrameTimestamp;
        this.lastVideoDataTimestamp = adjustedTimestamp;
        if (this.videoDataCounter % 100 === 0) {
            const timeDiff = this.lastAudioDataTimestamp - this.lastVideoDataTimestamp;
            console.log('Audio is ahead of video by ', Math.round(timeDiff / 1000), 'ms');
        }
        const videoFrame = {
            frameNumber: BigInt(this.videoDataCounter++),
            timestamp: BigInt(adjustedTimestamp),
            duration: videoData.duration,
            isKeyFrame: videoData.isKeyFrame,
            data: videoData.data,
            screenOrientation: screen.orientation.angle,
            extradata: videoData.extradata,
            codedWidth: videoData.codedWidth,
            codedHeight: videoData.codedHeight,
            codec: videoData.codec,
            isScreenSharing: this.screenSharing,
            videoOrientation: this.getVideoOrientationAngle(),
        };
        this.mediaPacketizer.addVideoFrame(videoFrame, this.currentMediaStreamId ?? '');
    }
    async onEncodedAudioData(audioData) {
        this.audioDataCounter++;
        if (this.firstFrameTimestamp === -1) {
            this.firstFrameTimestamp = Number(audioData.timestamp);
        }
        const adjustedTimestamp = Number(audioData.timestamp) - this.firstFrameTimestamp;
        this.lastAudioDataTimestamp = adjustedTimestamp;
        const audioFrame = {
            data: audioData.data,
            timestamp: BigInt(adjustedTimestamp),
            duration: BigInt(audioData.duration),
            frameNumber: BigInt(this.audioDataCounter),
            isKeyFrame: audioData.isKeyFrame,
            extradata: audioData.extradata,
        };
        this.mediaPacketizer.addAudioFrames([audioFrame], this.currentMediaStreamId ?? '');
    }
    stop() { }
    setVideoEncodingQuality(videoEncodingQuality) {
        this.videoEncodingQuality = videoEncodingQuality;
        this.videoEncoderWrapper.setVideoEncodingBitrate(videoEncodingQuality === 'sd'
            ? Settings.videoEncoderBitrateSd
            : Settings.videoEncoderBitrateHd);
    }
    getVideoEncodingQuality() {
        return this.videoEncodingQuality;
    }
    getVideoOrientationAngle() {
        /**
         * iOS camera sensors are natively landscape-oriented, but getUserMedia provides video
         * based on current device orientation. When capturing frames manually (e.g., via canvas),
         * we often need to rotate the image by 90° to match the expected orientation.
         *
         * Additionally, in landscape mode, the back camera feed may appear upside-down
         * and require an extra 180° rotation for correct display or encoding.
         *
         * Front cameras are mirrored by default, which may need correction as well.
         */
        if (!isIOS) {
            return 0;
        }
        if (screen.orientation.angle % 180 === 0 ||
            isUserFacingCameraTrack(this.videoEncoderWrapper.getTrack())) {
            return 90;
        }
        return 270;
    }
}
